import {Point} from "@/model/point";
import CU from "@/utils/c-u";
import {injectable} from "inversify";
import EducationPanel from "@/scenes.ui/education-panel";
import {ArrowMode, Education, HeroComment, Place, TriggerType} from "@/store/place/types";
import PlayerStore from "@/store/player";
import {PlayerMarker} from "@/store/player/types";
import EduWithButton from "@/scenes.ui/edu-with-button";
import Image = Phaser.GameObjects.Image;
import TimerEvent = Phaser.Time.TimerEvent;
import GameObject = Phaser.GameObjects.GameObject;
import Tween = Phaser.Tweens.Tween;
import TweenChain = Phaser.Tweens.TweenChain;

@injectable()
export class EducationService {
      private educationPanel!: EducationPanel|undefined;
      private educationPanelWithButton!: EduWithButton|undefined;
      private topDistanceTriggers: Map<number, DistanceTriggerData[]> = new Map();
      private bottomDistanceTriggers: Map<number, DistanceTriggerData[]> = new Map();
      private sleepingDelayTriggers: any = new Map();
      private activeDelayTriggers: any = new Map();
      private markerTriggers: MarkerTriggerData[] = [];
      private arrowAnims: any = new Map();
      private handAnims: any = new Map();
      private interactiveObjects: any = [];

      public busyTill: number | undefined = undefined;

      public interactiveObjectsHasKey(key: number): boolean {
            return this.interactiveObjects.includes(key);
      }

      public createEducationTrigger(place: Place, eduCallbacks: EducationServiceCallbacks) : void {
            place.educations.forEach((education: any) => {
                  const CALLBACK = this.createCallback(education, place, eduCallbacks);

                  switch (education.triggerType) {
                        case TriggerType.DELAY:
                              this.createDelayTrigger(place, education.delayMs, eduCallbacks.createDelayedCall, CALLBACK);
                              break;
                        case TriggerType.DISTANCE:
                              this.createTopDistanceTrigger(place, education, CALLBACK);
                              break;
                        case TriggerType.BATTLE_WON:
                              this.checkBattleWinTrigger(place, education, CALLBACK);
                              break;
                        case TriggerType.BATTLE_LOST:
                              this.checkBattleLostTrigger(place, CALLBACK);
                              break;
                        case TriggerType.PREMISS:
                              this.createBottomDistanceTrigger(place, education, CALLBACK);
                              break;
                        case TriggerType.MISS:
                              this.createMissDistanceTrigger(place, education, CALLBACK);
                              break;
                     }
            });
      }

      public createMarkerEducation(es: Education[], eduCallbacks: EducationServiceCallbacks) : void {
            if (es) {
                  for (let e of es) {
                        let exist = false;
                        for (let mt of this.markerTriggers) {
                              if (mt.id === e.id) {
                                    exist = true;
                                    break;
                              }
                        }
                        if (!exist) {
                              const callback = this.createCallback(e, undefined, eduCallbacks);
                              const triggerData: MarkerTriggerData = {
                                    id: e.id,
                                    requiredMarkers: e.requiredMarkers,
                                    forbiddenMarkers: e.forbiddenMarkers,
                                    callback
                              };
                              this.markerTriggers.push(triggerData);
                        }
                  }
            }
      }

      public destroyAllEducationTriggers(placeId: number): void {
            this.killDelayTrigger(placeId);
            this.topDistanceTriggers.delete(placeId);
            this.bottomDistanceTriggers.delete(placeId);
      }

      public onCompleteTrigger(educations: any, target: any, eduCallbacks: EducationServiceCallbacks): (() => unknown) | null {
            if (!educations) return null;

            const onCompleteTrigger = educations.filter((education: any) => education.triggerType === "COMPLETE");
            
            return onCompleteTrigger.length > 0
                        ? this.createCallback(onCompleteTrigger[0], target, eduCallbacks) : null;
      }

      public killEducationObjects(targetId: number, setMouseEnable: () => void): void {
            this.arrowAnims.has(targetId) && this.killArrowAnim(targetId);
            this.handAnims.has(targetId) && this.killHandAnim(targetId);
            this.interactiveObjects.includes(targetId) && this.deletInteractiveObject(targetId, setMouseEnable);
            this.educationPanel && this.educationPanel.destroy();
            this.educationPanelWithButton && this.educationPanelWithButton.destroy();
      }

      public checkTopDistanceTriggers(canvasHight: number, playerActualY: number) : void {            
            if (this.topDistanceTriggers.size) {
                  let self = this;
                  this.topDistanceTriggers.forEach((es: DistanceTriggerData[]) => {
                        for (let e of es) {
                              const CURRENT_TOP_COORDINATS: number = this.getTopWorldY(canvasHight, playerActualY);
                              if (e.targetObject.posY <= CURRENT_TOP_COORDINATS - e.distance && this.checkMarkers(e)) {
                                    this.doCallback(() => {
                                          e.callback();
                                          if (es.length === 1) {
                                                self.topDistanceTriggers.delete(e.targetObject.id);
                                          } else {
                                                self.topDistanceTriggers.set(e.targetObject.id, es.filter(p => p != e));
                                          }
                                    });
                                    return;
                              }
                        }
                  });
            }
      }

      private doCallback(callback: () => unknown) {
            let self = this;
            if (self.busyTill && new Date().getTime() < self.busyTill) {
                  let timerId1 = setInterval(() => {
                        callback();
                        clearInterval(timerId1);
                  }, self.busyTill - new Date().getTime());
            } else {
                  callback();
            }
      }

      public checkMarkerTriggers() : void {
            if (this.markerTriggers.length > 0) {
                  let self = this;
                  this.markerTriggers.forEach((e: MarkerTriggerData) => {
                        if (this.checkMarkers(e)) {
                              this.doCallback(() => {
                                    e.callback();
                                    self.markerTriggers = this.markerTriggers.filter(p => p != e);
                              });
                        }
                  });
            }
      }

      public checkBottomDistanceTriggers(playerActualY: number) : void {            
            if (this.bottomDistanceTriggers.size) {
                  let self = this;
                  this.bottomDistanceTriggers.forEach((es: DistanceTriggerData[]) => {
                        for (let e of es) {
                              if (e.targetObject.posY - 400 <= playerActualY + e.distance && this.checkMarkers(e)) {
                                    this.doCallback(() => {
                                          e.callback();
                                          if (es.length === 1) {
                                                self.bottomDistanceTriggers.delete(e.targetObject.id);
                                          } else {
                                                self.bottomDistanceTriggers.set(e.targetObject.id, es.filter(p => p != e));
                                          }
                                    });
                              }
                        }
                  });
            }
      }

      private checkMarkers(d: DistanceTriggerData | MarkerTriggerData): boolean {
            return this.doCheckMarkers(d.requiredMarkers, d.forbiddenMarkers)
      }

      private doCheckMarkers(requiredMarkers: String[], forbiddenMarkers: String[]): boolean {
            if (requiredMarkers) {
                  for (let m of requiredMarkers) {
                        if (!this.haveMarker(m)) {
                              return false;
                        }
                  }
            }
            if (forbiddenMarkers) {
                  for (let m of forbiddenMarkers) {
                        if (this.haveMarker(m)) {
                              return false;
                        }
                  }
            }
            return true;
      }

      private haveMarker(marker: String): Boolean {
            let pms = PlayerStore.getPlayer().playerMarkers;
            if (pms) {
                  return PlayerStore.getPlayer().playerMarkers.some((m: PlayerMarker) => m.playerMarker == marker);
            }
            return false;
      }

      public checkDelayTriggersPositions(playerActualY: number) : void {        
            if (this.sleepingDelayTriggers.size) {                  
                  this.sleepingDelayTriggers.forEach((e: DelayTriggerData, key: string) => {
                        if (e.targetPositionY - 150 <= playerActualY + CU.CANVAS_HEIGHT) {                              
                              e.trigger.paused = false;
                              this.activeDelayTriggers.set(key, e.trigger);
                              this.sleepingDelayTriggers.delete(key);
                        }
                  });
            }
      }

      private createEducationObjects(point: Point, education: Education, target: Place,
            createArrowAnim: (point: Point, arrowMode: ArrowMode, flipX?: boolean, flipY?: boolean, small?: boolean, arrowDelay?: number) => ArrowAnimData,
            createHandAnim: (point: Point) => HandAnimData,
            stopMoving: () => void,
            setMouseDisable: () => void): void {
            if (education.arrow && !this.arrowAnims.has(target.id)) {
                  const DATA: ArrowAnimData = createArrowAnim({x: point.x, y: point.y }, education.arrowMode, false, false, false);
                  this.arrowAnims.set(target.id, DATA);
            }
            if (education.roadHand && !this.handAnims.has(target.id)) {
                  const DATA: HandAnimData = createHandAnim(point);
                  this.handAnims.set(target.id, DATA);
            }
            if (education.stopMovement) stopMoving();
            if (education.forbidMovement) {
                  setMouseDisable();
                  this.interactiveObjects.push(target.id);
            }
      }

      private deletInteractiveObject(objectId: number, setMouseEnable: () => void): void {
            for (let i = 0; i < this.interactiveObjects.length; i++) {
                  this.interactiveObjects[i] === objectId && this.interactiveObjects.splice(i, 1);
            }
            setMouseEnable();
      }

      private killArrowAnim(targetId: number): void {
            const DATA: ArrowAnimData = this.arrowAnims.get(targetId);
            for (let go of DATA.gameObjects) {
                  go.destroy();
            }
            
            this.arrowAnims.delete(targetId);
      }

      private killHandAnim(targetId: number): void {
            const DATA: HandAnimData = this.handAnims.get(targetId);
            DATA.hand.destroy();
            DATA.shadow.destroy();
            DATA.handAnim.destroy();
            DATA.shadowAnim.destroy();
            
            this.handAnims.delete(targetId);
      }

      private createCallback(education: Education, target: Place | undefined, eduCallbacks: EducationServiceCallbacks): () => unknown {
            return (): unknown => {
                  if (target) {
                        const PLACE_POINT = this.calculateCanvasPoint(target, eduCallbacks.getActualY(), CU.CANVAS_HEIGHT);
                        PLACE_POINT.x += education.arrowX;
                        PLACE_POINT.y -= education.arrowY;

                        this.createEducationObjects(PLACE_POINT, education, target, eduCallbacks.createArrowAnim,
                            eduCallbacks.createHandAnim, eduCallbacks.stopMoving, eduCallbacks.setMouseDisable);
                  }

                  if (education.eduBlock) {
                        if (education.eduBlock.answer) {
                              const EDUCATION_PANEL = eduCallbacks.getEduWithButton(education.eduBlock.imageId, '', education.eduBlock.text,
                                  education.eduBlock.answer, education.postScriptId, target);
                              if (EDUCATION_PANEL) this.educationPanelWithButton = EDUCATION_PANEL;
                        } else {
                              this.createEducationPanel(education.eduBlock.text, education.eduBlock.imageId, eduCallbacks.getEducationPanel, eduCallbacks.killHeroComments);
                        }
                  }

                  education.heroComment && eduCallbacks.createHeroComment(education.heroComment, education.delayMs, education.heroComment.commentDuration!);

                  education.scriptId && eduCallbacks.launchScript(target, education.scriptId);
                  education.giveMarker && this.giveMarker(education.giveMarker);
                  education.removeMarker && this.removeMarker(education.removeMarker);

                  return undefined;
            };
      }

      private createDelayTrigger(targetObject: Place, delayTime: number,
            createDelayedCall: (delayTime: number, callback: () => unknown) => TimerEvent,
            callback: () => unknown) : void {
            const DELAY_TRIGGER: TimerEvent = createDelayedCall(delayTime, () => {
                  this.activeDelayTriggers.delete(targetObject.id);
                  callback();
            });
            DELAY_TRIGGER.paused =  true;
            const TRIGGER_DATA: DelayTriggerData = { targetPositionY: targetObject.posY, trigger: DELAY_TRIGGER };
            this.sleepingDelayTriggers.set(targetObject.id, TRIGGER_DATA);
      }

      private killDelayTrigger(targetId: number): void {
            const TRIGGER: TimerEvent = this.activeDelayTriggers.get(targetId);
            TRIGGER && TRIGGER.destroy();
            this.activeDelayTriggers.delete(targetId);
      }

      private createEducationPanel(educationText: string, speakerFrameKey: string,
            getEducationPanel: (educationText: string, speakerFrameKey: string) => EducationPanel|undefined,
                                   killHeroCommens: () => unknown): void {
            killHeroCommens();
            const EDUCATION_PANEL = getEducationPanel(educationText, speakerFrameKey);
            if (EDUCATION_PANEL) this.educationPanel = EDUCATION_PANEL;
      }

      private createTopDistanceTrigger(targetObject: Place, e: Education, callback: () => unknown) : void {
            const triggerData: DistanceTriggerData = {targetObject,
                  distance: e.distanceFromTopOfScreen, requiredMarkers: e.requiredMarkers, forbiddenMarkers: e.forbiddenMarkers,
                  callback};
            if (!this.topDistanceTriggers.has(targetObject.id)) {
                  this.topDistanceTriggers.set(targetObject.id, []);
            }

            let haveAlready = false;
            this.topDistanceTriggers.get(targetObject.id)!.forEach(t => {
                  if (t.distance === triggerData.distance) {
                        haveAlready = true;
                  }
            });
            if (!haveAlready) {
                  this.topDistanceTriggers.get(targetObject.id)!.push(triggerData);
            }
      }

      private createBottomDistanceTrigger(targetObject: Place, e: Education, callback: () => unknown) : void {
            const triggerData: DistanceTriggerData = {targetObject,
                  distance: e.distanceFromTopOfScreen, requiredMarkers: e.requiredMarkers, forbiddenMarkers: e.forbiddenMarkers,
                  callback};
            if (!this.bottomDistanceTriggers.has(targetObject.id)) {
                  this.bottomDistanceTriggers.set(targetObject.id, []);
            }

            let haveAlready = false;
            this.bottomDistanceTriggers.get(targetObject.id)!.forEach(t => {
                  if (t.distance === triggerData.distance) {
                        haveAlready = true;
                  }
            });
            if (!haveAlready) {
                  this.bottomDistanceTriggers.get(targetObject.id)!.push(triggerData);
            }
      }

      private calculateCanvasPoint(place: Place, actualY: number, canvasHeight: number): Point {
            const POS_Y = this.worldYToCanvasY(place.posY, actualY, canvasHeight);
            const POS_X = (place.posX  * CU.S) - 10;
            return new Point(POS_X, POS_Y);
      }

      private createMissDistanceTrigger(targetObject: Place, e: Education, callback: () => unknown) : void {
            const triggerData: DistanceTriggerData = {targetObject,
                  distance: -550, requiredMarkers: e.requiredMarkers, forbiddenMarkers: e.forbiddenMarkers,
                  callback};

            if (!this.bottomDistanceTriggers.has(targetObject.id)) {
                  this.bottomDistanceTriggers.set(targetObject.id, []);
            }

            let haveAlready = false;
            this.bottomDistanceTriggers.get(targetObject.id)!.forEach(t => {
                  if (t.distance === triggerData.distance) {
                        haveAlready = true;
                  }
            });
            if (!haveAlready) {
                  this.bottomDistanceTriggers.get(targetObject.id)!.push(triggerData);
            }
      }

      private checkBattleLostTrigger(targetObject: any, callback: () => unknown): void {
            if (targetObject.battleLost === true) callback();
      }

      private checkBattleWinTrigger(targetObject: any, e: Education, callback: () => unknown): void {
            if (!targetObject.creatureId && this.doCheckMarkers(e.requiredMarkers, e.forbiddenMarkers)) {
                  callback();
            }
      }

      private getTopWorldY(canvasHight: number, playerActualY: number) : number {            
            return playerActualY + canvasHight / CU.S ;
      }

      public worldYToCanvasY(worldY: number, actualY: number, canvasHeight: number): number {
            return canvasHeight - ((worldY - actualY) * CU.S);
      }

      private giveMarker(marker: string) {
            PlayerStore.SET_MARKER(marker);
            PlayerStore.setMarker(marker);
      }

      private removeMarker(marker: string) {
            PlayerStore.REMOVE_MARKER(marker);
            PlayerStore.removeMarker(marker);
      }
}

export type DistanceTriggerData = {
      targetObject: Place;
      distance: number;
      requiredMarkers: string[];
      forbiddenMarkers: string[];
      callback: () => unknown;
}

export type MarkerTriggerData = {
      id: string;
      requiredMarkers: string[];
      forbiddenMarkers: string[];
      callback: () => unknown;
}

export type DelayTriggerData = {
      targetPositionY: number;
      trigger: TimerEvent;
}

export type ArrowAnimData = {
      gameObjects: (GameObject | Tween | TweenChain)[];
}

export type HandAnimData = {
      hand: Image,
      shadow: Image,
      handAnim: any,
      shadowAnim: any
}

export type EducationServiceCallbacks = {
      createDelayedCall: (delayTime: number, callback: () => unknown) => TimerEvent,
      getActualY: () => number,
      launchScript: (place: Place | undefined, id: string) => unknown,
      createHeroComment: (text: HeroComment, delay: number, animDuration: number) => unknown,
      killHeroComments: () => unknown,
      createArrowAnim: (point: Point, arrowMode: ArrowMode, flipX?: boolean, flipY?: boolean, small?: boolean, arrowDelay?: number) => ArrowAnimData,
      createHandAnim: (point: Point) => HandAnimData,
      stopMoving: () => void,
      setMouseDisable: () => void,
      getEducationPanel: (educationText: string, speakerFrameKey: string) => EducationPanel|undefined,
      getEduWithButton: (teacherKey: string, title: string, text: string, answer: string, postScriptId: string | undefined, place: Place | undefined) => EduWithButton|undefined
}